<template>
  <div
    ref="slider"
    class="variant-slider"
    :class="{
      'variant-slider--images-loaded': areImagesLoaded,
      'variant-slider--navigation-mode': isNavigationMode,
    }"
    v-data-test-id="'variant-slider'"
  >
    <div v-if="label" class="variant-slider__slider-label">
      {{ $t(`filter.variant_slider.${label}`) }}
    </div>
    <div
      class="variant-slider__slider"
      :class="{
        'variant-slider__slider--navigation-shown': isNavigationShown,
      }"
    >
      <div
        v-if="isNavigationMode"
        class="variant-slider__navigation variant-slider__navigation--prev"
        :class="{ 'variant-slider__navigation--disabled': navigateToIndex === 0 }"
        :style="prevButtonStyle"
        @click="navigate('prev')"
        v-data-test-id="'navigation-icon'"
      >
        <IconForward class="variant-slider__navigation-icon" />
      </div>
      <Carousel
        ref="carousel"
        class="variant-slider__carousel"
        :navigateTo="navigateToIndex"
        :paginationEnabled="false"
        :perPage="perPage"
        :scrollPerPage="!isNavigationMode"
        :spacePadding="spacePadding"
        :speed="300"
        :style="`margin-left: -${spacePadding + imagePadding}px; width: ${carouselWidth}`"
        :touchDrag="!isNavigationMode"
      >
        <Slide
          v-for="(variant, slideIndex) in variants"
          :key="slideIndex"
          class="variant-slider__carousel-slide"
          @slide-click="onSlideClick(variant, slideIndex, sliderId)"
          v-data-test-id="'variant-slider-slide'"
        >
          <div class="variant-slider__carousel-slide-inner">
            <ImageContentful
              class="variant-slider__carousel-image"
              :class="{
                'variant-slider__carousel-image--selected':
                  isActive && currentVariantIndex === slideIndex,
              }"
              :src="variant.src"
              :options="{
                backgroundColor: 'f7f7f7',
                fixedWidth: 50,
                fixedHeight: 50,
                format: 'jpg',
                imageCompression: 95,
                imageSizes: [50],
                retinaSupportFactor: 1,
              }"
            />
          </div>
        </Slide>
      </Carousel>

      <div
        v-if="isNavigationMode"
        class="variant-slider__navigation variant-slider__navigation--next"
        :class="{ 'variant-slider__navigation--disabled': isLastPage }"
        @click="navigate('next')"
        v-data-test-id="'navigation-icon'"
      >
        <IconForward class="variant-slider__navigation-icon" />
      </div>
    </div>
  </div>
</template>

<script>
// TODO: B2C-2268 Go back to original version of vue-carousel after bug PR got merged
import { Carousel, Slide } from '@/components/_shared/Vue-Carousel-Fix'
import ImageContentful from '@/components/_shared/ImageContentful.vue'
import IconForward from '@/components/_icons/IconForward.svg'

const imagePadding = 2

export default {
  name: 'VariantSlider',
  components: { Carousel, IconForward, ImageContentful, Slide },
  props: {
    currentVariantIndex: {
      type: Number,
      default: 0,
    },
    isActive: {
      type: Boolean,
      default: true,
    },
    isNavigationMode: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    slideMargin: {
      type: Number,
      default: 4,
    },
    sliderId: {
      type: Number,
      default: 0,
    },
    slidesPerPage: {
      type: Number,
      default: 5,
    },
    spacePadding: {
      type: Number,
      default: 0,
    },
    variants: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      areImagesLoaded: false,
      imagePadding,
      navigateToIndex: 0,
    }
  },

  computed: {
    carouselWidth() {
      const widthInNavigationMode = `${(100 / this.slidesPerPage) * (this.slidesPerPage - 1)}%`
      return this.isNavigationShown ? widthInNavigationMode : ''
    },

    isLastPage() {
      return this.variants.length < this.navigateToIndex + this.slidesPerPage
    },

    isNavigationShown() {
      return this.variants.length > this.slidesPerPage - 1 && this.isNavigationMode
    },

    perPage() {
      return this.isNavigationShown ? this.slidesPerPage - 1 : this.slidesPerPage
    },

    prevButtonStyle() {
      const width = `width: calc(${100 / this.slidesPerPage}% - 3px);`
      const height = `height: calc(100% - ${this.slideMargin + imagePadding * 2}px);`
      return width + height
    },
  },

  mounted() {
    const slider = this.$refs.slider
    if (slider) imagesLoaded(slider, () => (this.areImagesLoaded = true))
  },

  methods: {
    onSlideClick(medium, slideIndex, sliderId) {
      this.$emit('slide-click', medium.sku, slideIndex, sliderId)
    },

    navigate(direction) {
      const slideDirection = direction === 'next' ? 1 : -1
      const nextIndex = this.navigateToIndex + (this.slidesPerPage - 2) * slideDirection
      if (nextIndex >= 0 && nextIndex < this.variants.length) this.navigateToIndex = nextIndex
    },
  },
}
</script>

<style lang="scss" scoped>
.variant-slider {
  opacity: 0;
  transition: opacity $transition-time-slower;
  flex: 1;
  font-family: $font-family-helvetica-now;

  &--navigation-mode {
    transition: none;
  }

  &--images-loaded {
    opacity: 1;
  }

  &__slider {
    position: relative;
    overflow: hidden;

    &--navigation-shown {
      display: flex;
    }
  }

  &__slider-label {
    margin: 15px 0 5px;
    font-size: $f-size-xxxsmall;
    font-weight: bold;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: $c-grey-dark-1;
  }

  &__carousel-slide-inner {
    margin: 4px 2px;
  }

  &__carousel-image {
    display: block;
    padding: 5px;
    background-color: $c-grey-light-3;
    border: 1px solid $c-grey-light-3;
    transition: border-color $transition-time;

    &:hover {
      border: 1px solid $c-grey-light-2;
    }

    &--selected {
      border: 1px solid $c-grey-dark-1;

      &:hover {
        border: 1px solid $c-grey-dark-1;
      }
    }
  }

  &__navigation {
    display: flex;
    flex: 1;
    flex-grow: 1;
    align-items: center;
    justify-content: center;
    margin: 4px 2px;
    border: 1px solid $c-grey-light-3;
    transition: border-color $transition-time;

    &:hover {
      border-color: $c-grey-light-1;
    }

    &--next {
      margin-right: 0;
    }

    &--prev {
      position: absolute;
      z-index: 1;
      top: 4px;
      left: 0;
      bottom: 0;
      margin: 0;
      background-color: $color-white;
      transform: rotate(180deg);
    }

    &--disabled {
      display: none;
    }
  }

  &__navigation-icon {
    width: 10px;
    height: 10px;
  }
}
</style>
